import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { usePublic } from "@/pages/public/PublicLayout";
import api, { fileUrl } from "@/lib/api";
import { Utensils, Wine, CalendarClock, ChevronRight, Instagram, Facebook, MessageSquareHeart, ExternalLink, Globe, Wifi, Copy, Check } from "lucide-react";

const TripAdvisorIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12 6.5c-2.6 0-5 .6-7.1 1.7H1l1.6 1.8A4 4 0 0 0 9.1 15l1.4 1.6L12 15l1.5 1.6 1.4-1.6a4 4 0 0 0 6.5-3.9L23 8.2h-3.9C17 7.1 14.6 6.5 12 6.5Zm-5.4 8a2.4 2.4 0 1 1 0-4.8 2.4 2.4 0 0 1 0 4.8Zm10.8 0a2.4 2.4 0 1 1 0-4.8 2.4 2.4 0 0 1 0 4.8ZM6.6 11a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2Zm10.8 0a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2Z" />
  </svg>
);

const HOME_T = {
  it: { special: "Speciale", empty: "Nessun menù disponibile.", reviewTitle: "Lascia una recensione", reviewSub: "Il tuo parere ci aiuta a migliorare", wifi: "Wi-Fi", wifiNet: "Rete", wifiPass: "Password", copied: "Copiato!" },
  en: { special: "Special", empty: "No menu available.", reviewTitle: "Leave a review", reviewSub: "Your feedback helps us improve", wifi: "Wi-Fi", wifiNet: "Network", wifiPass: "Password", copied: "Copied!" },
  fr: { special: "Spécial", empty: "Aucun menu disponible.", reviewTitle: "Laisser un avis", reviewSub: "Votre avis nous aide à nous améliorer", wifi: "Wi-Fi", wifiNet: "Réseau", wifiPass: "Mot de passe", copied: "Copié !" },
  de: { special: "Spezial", empty: "Kein Menü verfügbar.", reviewTitle: "Bewertung abgeben", reviewSub: "Ihr Feedback hilft uns, besser zu werden", wifi: "WLAN", wifiNet: "Netzwerk", wifiPass: "Passwort", copied: "Kopiert!" },
  es: { special: "Especial", empty: "Ningún menú disponible.", reviewTitle: "Deja una reseña", reviewSub: "Tu opinión nos ayuda a mejorar", wifi: "Wi-Fi", wifiNet: "Red", wifiPass: "Contraseña", copied: "¡Copiado!" },
  pt: { special: "Especial", empty: "Nenhum menu disponível.", reviewTitle: "Deixe uma avaliação", reviewSub: "A sua opinião ajuda-nos a melhorar", wifi: "Wi-Fi", wifiNet: "Rede", wifiPass: "Palavra-passe", copied: "Copiado!" },
};

const RestaurantHome = () => {
  const { data, theme, lang, slug } = usePublic();
  const navigate = useNavigate();
  const tr = HOME_T[lang] || HOME_T.it;
  const menus = (data.menus || []).filter((m) => m.home_visible !== false);
  const bs = data.settings || {};
  const bannerOn = bs.banner_enabled && bs.banner_image;
  const bannerHref = (bs.banner_link_type === "pdf")
    ? (bs.banner_link_pdf ? fileUrl(bs.banner_link_pdf) : null)
    : ((bs.banner_link_url || "").trim() || null);

  const icon = (m) => (m.is_daily ? CalendarClock : m.type === "bevande" ? Wine : Utensils);

  const openMenu = (m) => {
    api.post(`/public/${slug}/menu/${m.id}/view`).catch(() => {});
    if (m.link_type === "external" && m.link_url) { window.open(m.link_url, "_blank", "noopener,noreferrer"); return; }
    if (m.link_type === "pdf" && m.link_pdf) { window.open(fileUrl(m.link_pdf), "_blank", "noopener,noreferrer"); return; }
    navigate(`/m/${slug}/menu/${m.id}`);
  };

  return (
    <div className="pt-10 sm:pt-16">
      <div className="space-y-4">
        {menus.map((m, i) => {
          const Icon = icon(m);
          return (
            <motion.button
              key={m.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              onClick={() => openMenu(m)}
              data-testid={`home-menu-${m.id}`}
              className="group glow-hover relative w-full h-40 rounded-2xl overflow-hidden text-left"
              style={{ border: `1px solid ${theme.accent}40`, "--glow": theme.accent, "--glow-soft": `${theme.accent}55` }}
            >
              {m.cover_image ? (
                <img src={fileUrl(m.cover_image)} alt={m.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 group-active:scale-105" />
              ) : (
                <div className="absolute inset-0" style={{ backgroundColor: theme.surface }} />
              )}
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${theme.bg}ee 0%, ${theme.bg}55 55%, transparent 100%)` }} />
              <div className="absolute inset-0 p-5 flex flex-col justify-end">
                {m.is_daily && <span className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: theme.accentLight }}>{tr.special}</span>}
                <div className="flex items-center gap-2">
                  <Icon className="w-5 h-5" style={{ color: theme.accent }} />
                  <span className="text-2xl font-medium" style={{ fontFamily: theme.heading, color: theme.text }}>{(lang !== "it" && m.translations?.[lang]?.name) || m.name}</span>
                  {(m.link_type === "external" || m.link_type === "pdf") ? (
                    <ExternalLink className="w-5 h-5 ml-auto transition-transform group-hover:translate-x-1" style={{ color: theme.accent }} />
                  ) : (
                    <ChevronRight className="w-5 h-5 ml-auto transition-transform group-hover:translate-x-1" style={{ color: theme.accent }} />
                  )}
                </div>
              </div>
            </motion.button>
          );
        })}
        {menus.length === 0 && <p className="text-center py-10" style={{ color: theme.muted }}>{tr.empty}</p>}
      </div>

      {/* Separatore + card recensioni */}
      <div className="mt-10 flex items-center gap-4" aria-hidden="true">
        <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}40, transparent)` }} />
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.accent }} />
        <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}40, transparent)` }} />
      </div>

      <motion.button
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        onClick={() => navigate(`/m/${slug}/recensioni`)}
        data-testid="home-reviews-btn"
        className="group glow-hover w-full mt-6 rounded-2xl px-5 py-4 flex items-center gap-3 text-left transition-transform active:scale-[0.99]"
        style={{ border: `1px solid ${theme.accent}55`, backgroundColor: theme.surface, "--glow": theme.accent, "--glow-soft": `${theme.accent}55` }}
      >
        <span className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: theme.accent + "1f", color: theme.accent }}>
          <MessageSquareHeart className="w-5 h-5" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-lg font-medium leading-tight" style={{ fontFamily: theme.heading, color: theme.text }}>{tr.reviewTitle}</span>
          <span className="block text-xs mt-0.5" style={{ color: theme.muted }}>{tr.reviewSub}</span>
        </span>
        <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" style={{ color: theme.accent }} />
      </motion.button>

      {bannerOn && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-12" data-testid="home-ad-banner">
          {bannerHref ? (
            <a href={bannerHref} target="_blank" rel="noopener noreferrer" data-testid="home-ad-banner-link" className="block w-full rounded-2xl overflow-hidden transition-transform active:scale-[0.99]" style={{ border: `1px solid ${theme.accent}40` }}>
              <img src={fileUrl(bs.banner_image)} alt="" className="w-full h-auto object-cover" />
            </a>
          ) : (
            <div className="block w-full rounded-2xl overflow-hidden" style={{ border: `1px solid ${theme.accent}40` }}>
              <img src={fileUrl(bs.banner_image)} alt="" className="w-full h-auto object-cover" />
            </div>
          )}
        </motion.div>
      )}

      {(data.settings?.instagram_url || data.settings?.facebook_url || data.settings?.website_url || data.settings?.tripadvisor_url) && (
        <div className="flex items-center justify-center gap-6 mt-12 flex-wrap" data-testid="public-social">
          {data.settings?.instagram_url && (
            <a href={data.settings.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram" className="hover:opacity-70 transition-opacity" style={{ color: theme.accent }}>
              <Instagram className="w-6 h-6" />
            </a>
          )}
          {data.settings?.facebook_url && (
            <a href={data.settings.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook" className="hover:opacity-70 transition-opacity" style={{ color: theme.accent }}>
              <Facebook className="w-6 h-6" />
            </a>
          )}
          {data.settings?.tripadvisor_url && (
            <a href={data.settings.tripadvisor_url} target="_blank" rel="noopener noreferrer" data-testid="social-tripadvisor" aria-label="TripAdvisor" className="hover:opacity-70 transition-opacity" style={{ color: theme.accent }}>
              <TripAdvisorIcon className="w-6 h-6" />
            </a>
          )}
          {data.settings?.website_url && (
            <a href={data.settings.website_url} target="_blank" rel="noopener noreferrer" data-testid="social-website" aria-label="Sito web" className="hover:opacity-70 transition-opacity" style={{ color: theme.accent }}>
              <Globe className="w-6 h-6" />
            </a>
          )}
        </div>
      )}

      {(data.settings?.wifi_name || data.settings?.wifi_password) && (
        <WifiCard settings={data.settings} theme={theme} tr={tr} />
      )}
    </div>
  );
};

const WifiCard = ({ settings, theme, tr }) => {
  const [copied, setCopied] = useState(null);
  const copy = (val, key) => {
    if (!val) return;
    navigator.clipboard?.writeText(val).then(() => {
      setCopied(key);
      toast.success(tr.copied);
      setTimeout(() => setCopied(null), 1500);
    }).catch(() => {});
  };
  return (
    <div className="mt-8 max-w-sm mx-auto rounded-2xl p-5" data-testid="public-wifi" style={{ border: `1px solid ${theme.accent}33`, background: `${theme.accent}0d` }}>
      <div className="flex items-center justify-center gap-2 mb-4" style={{ color: theme.accent }}>
        <Wifi className="w-5 h-5" />
        <span className="font-semibold text-sm uppercase tracking-wide">{tr.wifi}</span>
      </div>
      <div className="space-y-3">
        {settings.wifi_name && (
          <button onClick={() => copy(settings.wifi_name, "net")} data-testid="wifi-copy-net" className="w-full flex items-center justify-between gap-3 rounded-xl px-4 py-2.5 text-left transition-colors" style={{ border: `1px solid ${theme.accent}22` }}>
            <span className="min-w-0">
              <span className="block text-[11px] uppercase tracking-wide opacity-60">{tr.wifiNet}</span>
              <span className="block font-medium truncate">{settings.wifi_name}</span>
            </span>
            {copied === "net" ? <Check className="w-4 h-4 shrink-0" style={{ color: theme.accent }} /> : <Copy className="w-4 h-4 shrink-0 opacity-50" />}
          </button>
        )}
        {settings.wifi_password && (
          <button onClick={() => copy(settings.wifi_password, "pass")} data-testid="wifi-copy-pass" className="w-full flex items-center justify-between gap-3 rounded-xl px-4 py-2.5 text-left transition-colors" style={{ border: `1px solid ${theme.accent}22` }}>
            <span className="min-w-0">
              <span className="block text-[11px] uppercase tracking-wide opacity-60">{tr.wifiPass}</span>
              <span className="block font-medium truncate">{settings.wifi_password}</span>
            </span>
            {copied === "pass" ? <Check className="w-4 h-4 shrink-0" style={{ color: theme.accent }} /> : <Copy className="w-4 h-4 shrink-0 opacity-50" />}
          </button>
        )}
      </div>
    </div>
  );
};

export default RestaurantHome;
